import React, { forwardRef, useImperativeHandle } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';

import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { InventoryReturnRow } from '../types';

export type ReturnListHandle = {
  requestRefresh: () => void;
};

interface ReturnListProps {
  rows: InventoryReturnRow[];
  loading?: boolean;
  onSelectRow?: (row: InventoryReturnRow) => void;
  onRefresh?: () => void;
}

const statusColor = (status: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  switch (status) {
    case 'posted':
      return 'success';
    case 'draft':
      return 'warning';
    case 'approved':
      return 'info';
    default:
      return 'default';
  }
};

const ReturnList = forwardRef<ReturnListHandle, ReturnListProps>(function ReturnList(
  { rows, loading = false, onSelectRow, onRefresh },
  ref
) {
  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => onRefresh?.(),
    }),
    [onRefresh]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Không có phiếu hoàn trả</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc tạo phiếu mới.</Typography>
      </Box>
    );
  }

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryReturnRow) => React.ReactNode;
  }> = [
    {
      id: 'status',
      label: 'Trạng thái',
      width: '140px',
      render: (_: unknown, row: InventoryReturnRow) => <Chip size='small' color={statusColor(row.status)} label={row.status} />,
    },
    { id: 'code', label: 'Mã hoàn trả', width: '160px', render: (_: unknown, row: InventoryReturnRow) => <Typography sx={{ fontWeight: 600 }}>{row.code}</Typography> },
    { id: 'name', label: 'Tên hoàn trả', width: '240px' },
    { id: 'order_no', label: 'Đơn hàng', width: '160px' },
    { id: 'store_name', label: 'Kho', width: '200px' },
    { id: 'qty', label: 'SL hoàn', width: '140px', align: 'right', render: (_: unknown, row: InventoryReturnRow) => row.qty.toLocaleString('vi-VN') },
    { id: 'company_name', label: 'Công ty', width: '220px' },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={() => undefined}
        onSelectAll={() => undefined}
        onRowClick={(row) => onSelectRow?.(row as InventoryReturnRow)}
        getRowId={(row) => (row as InventoryReturnRow).id}
        isRowSelected={() => false}
        areAllRowsSelected={false}
        areSomeRowsSelected={false}
        showCheckbox={false}
      />
    </Box>
  );
});

export default ReturnList;
